import { Link } from 'react-router-dom';
import { categories, events } from '@/mocks/events';
import { hotKeywords } from '@/mocks/site';
import SectionHeading from '@/components/base/SectionHeading';

export default function HotRankSection() {
  const ranked = [...events].sort((a, b) => b.heat - a.heat).slice(0, 8);
  const maxHeat = ranked[0]?.heat ?? 1;

  return (
    <section className="bg-background-100 py-14 md:py-20">
      <div className="mx-auto w-full px-4 md:px-6 lg:px-10">
        <SectionHeading
          eyebrow="今日热榜"
          title="此刻全校都在看什么"
          desc="按实时热度排序，数字越大代表讨论越集中。点进去可以看到完整的来龙去脉。"
          action={{ label: '查看全部事件', to: '/events' }}
        />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <ol className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              {ranked.map((event, index) => (
                <li key={event.id} className="border-b border-background-200 last:border-b-0">
                  <Link
                    to={`/events/${event.id}`}
                    className="group flex items-center gap-3 px-4 py-4 transition-colors hover:bg-background-100 md:gap-5 md:px-6"
                  >
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md font-heading text-sm font-black md:h-9 md:w-9 ${
                        index < 3 ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-600'
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-heading text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-600 md:text-base">
                        {event.title}
                      </span>
                      <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-400">
                        <span className="rounded-full bg-accent-100 px-2 py-0.5 font-medium text-accent-900">
                          {event.category}
                        </span>
                        <span>{event.campus}</span>
                        <span>讨论 {event.comments}</span>
                      </span>
                    </span>
                    <span className="hidden w-28 shrink-0 flex-col items-end gap-1.5 sm:flex">
                      <span className="flex items-center gap-1 text-xs font-semibold text-primary-600">
                        <i className="ri-fire-fill text-sm leading-none" aria-hidden="true"></i>
                        {event.heat.toLocaleString()}
                      </span>
                      <span className="block h-1.5 w-full overflow-hidden rounded-full bg-background-200">
                        <span
                          className="block h-full rounded-full heat-bar"
                          style={{ width: `${Math.max((event.heat / maxHeat) * 100, 12)}%` }}
                        ></span>
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-search-line text-lg leading-none text-primary-500" aria-hidden="true"></i>
                大家都在搜
              </h3>
              <ul className="mt-4 flex flex-wrap gap-2">
                {hotKeywords.map((word) => (
                  <li key={word}>
                    <Link
                      to={`/events?q=${encodeURIComponent(word)}`}
                      className="inline-block rounded-full bg-background-100 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
                    >
                      {word}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-foreground-950 p-5 text-background-100 md:p-6">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-background-50">
                <i className="ri-thumb-up-line text-lg leading-none text-accent-400" aria-hidden="true"></i>
                围观守则
              </h3>
              <ul className="mt-4 space-y-3 text-sm text-background-200">
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-base leading-none text-accent-400" aria-hidden="true"></i>
                  先看时间线，再下结论
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-base leading-none text-accent-400" aria-hidden="true"></i>
                  就事论事，不指向个人
                </li>
                <li className="flex gap-2">
                  <i className="ri-check-line mt-0.5 text-base leading-none text-accent-400" aria-hidden="true"></i>
                  有后续欢迎随时补充投稿
                </li>
              </ul>
              <Link
                to="/about"
                className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-accent-300 transition-colors hover:text-accent-200"
              >
                了解我们的内容原则
                <i className="ri-arrow-right-line text-base leading-none" aria-hidden="true"></i>
              </Link>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h3 className="font-heading text-base font-bold text-foreground-950">热门分类</h3>
              <ul className="mt-3 space-y-1">
                {categories.slice(0, 5).map((cat) => (
                  <li key={cat.id}>
                    <Link
                      to={`/events?cat=${cat.id}`}
                      className="flex items-center justify-between rounded-md px-2 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
                    >
                      <span className="flex items-center gap-2">
                        <i className={`${cat.icon} text-base leading-none text-primary-500`} aria-hidden="true"></i>
                        {cat.name}
                      </span>
                      <i className="ri-arrow-right-s-line text-base leading-none text-foreground-300" aria-hidden="true"></i>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}